/* eslint-disable quotes, linebreak-style */
const syncBodyState = () => {
  const hasOpenPopup = Boolean(document.querySelector(".estimated-workdays__popup.is-open"));

  document.body.classList.toggle("overflow-hidden", hasOpenPopup);
  document.body.classList.toggle("lgc-popup-open", hasOpenPopup);
};

const closePopup = (popup) => {
  popup.classList.remove("is-open");
  popup.setAttribute("aria-hidden", "true");
  syncBodyState();
};

const openPopup = (popup) => {
  document.querySelectorAll(".estimated-workdays__popup.is-open").forEach((openModal) => {
    closePopup(openModal);
  });

  popup.classList.add("is-open");
  popup.setAttribute("aria-hidden", "false");
  syncBodyState();
};

const initEstimatedWorkdaysPopups = (scope = document) => {
  const cards = scope.querySelectorAll(".lgc-pricing-card");

  cards.forEach((card, index) => {
    if (card.dataset.lgcPopupInit === "true") {
      return;
    }

    card.dataset.lgcPopupInit = "true";

    const triggers = card.querySelectorAll(".modal-trigger");
    const popups = card.querySelectorAll(".estimated-workdays__popup");

    if (!triggers.length || !popups.length) {
      return;
    }

    popups.forEach((popup, popupIndex) => {
      // Keep popups outside card stacking contexts so hover effects cannot affect them.
      if (popup.parentElement !== document.body) {
        document.body.appendChild(popup);
      }

      const title = popup.querySelector(".estimated-workdays__header h2");
      if (title) {
        const titleId = `estimated-workdays-title-${Date.now()}-${index + 1}-${popupIndex + 1}`;
        title.id = titleId;
        popup.setAttribute("aria-labelledby", titleId);
      }
    });

    triggers.forEach((trigger, triggerIndex) => {
      const popup = popups[triggerIndex];
      const closeButton = popup?.querySelector(".estimated-workdays__close");

      if (!popup || !closeButton) {
        return;
      }

      if (!trigger.hasAttribute("tabindex")) {
        trigger.setAttribute("tabindex", "0");
      }
      if (!trigger.hasAttribute("role")) {
        trigger.setAttribute("role", "button");
      }

      trigger.addEventListener("click", () => {
        openPopup(popup);
      });

      trigger.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openPopup(popup);
        }
      });

      closeButton.addEventListener("click", () => {
        closePopup(popup);
        trigger.focus();
      });

      popup.addEventListener("click", (event) => {
        if (event.target === popup) {
          closePopup(popup);
        }
      });
    });
  });
};

// Theme editor only: lets staff preview popup wording before saving it in the section settings.
// Always scans the document because popups are relocated to <body> on init.
const initPopupTextEditors = () => {
  if (!window.Shopify || !window.Shopify.designMode) {
    return;
  }

  document.querySelectorAll("[data-lgc-editable-item]").forEach((item) => {
    if (item.dataset.lgcEditInit === "true") {
      return;
    }

    const textEl = item.querySelector("[data-lgc-text]");
    const editBtn = item.querySelector("[data-lgc-edit-toggle]");
    const editForm = item.querySelector("[data-lgc-edit-form]");
    const editInput = item.querySelector("[data-lgc-edit-input]");
    const saveBtn = item.querySelector("[data-lgc-edit-save]");
    const cancelBtn = item.querySelector("[data-lgc-edit-cancel]");

    if (!textEl || !editBtn || !editForm || !editInput || !saveBtn || !cancelBtn) {
      return;
    }

    item.dataset.lgcEditInit = "true";

    const closeEditor = () => {
      editForm.hidden = true;
      editBtn.setAttribute("aria-expanded", "false");
    };

    const openEditor = () => {
      editInput.value = textEl.textContent.trim();
      editForm.hidden = false;
      editBtn.setAttribute("aria-expanded", "true");
      editInput.focus();
    };

    editBtn.setAttribute("aria-expanded", "false");

    editBtn.addEventListener("click", () => {
      if (editForm.hidden) {
        openEditor();
      } else {
        closeEditor();
      }
    });

    saveBtn.addEventListener("click", () => {
      const newText = editInput.value.trim();

      if (newText) {
        textEl.textContent = newText;
      }

      closeEditor();
    });

    cancelBtn.addEventListener("click", closeEditor);

    editInput.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeEditor();
      }
    });
  });
};

const initPricingTierContext = (scope = document) => {
  const links = scope.querySelectorAll("[data-lgc-tier-link]");

  links.forEach((link) => {
    if (link.dataset.lgcTierContextInit === "true") {
      return;
    }

    link.dataset.lgcTierContextInit = "true";

    link.addEventListener("click", (event) => {
      if (link.dataset.lgcLocked === "true") {
        event.preventDefault();
        return;
      }

      const card = link.closest(".lgc-pricing-card");

      if (!card || !window.sessionStorage) {
        return;
      }

      const payload = {
        title: card.dataset.tierName || "",
        price: card.dataset.tierPrice || "",
        estimatedDays: card.dataset.tierEstimatedDays || "",
        maxValue: card.dataset.tierMaxValue || "",
        minCards: card.dataset.tierMinCards || "",
        includesTcg: card.dataset.tierIncludesTcg === "true",
        includesSports: card.dataset.tierIncludesSports === "true",
        handlingFeeVariantId: card.dataset.tierHandlingFeeVariantId || "",
        handlingFeePrice: card.dataset.tierHandlingFeePrice || "",
        handlingFeeTitle: card.dataset.tierHandlingFeeTitle || "",
        targetUrl: link.getAttribute("href") || "",
      };

      window.sessionStorage.setItem("lgcSelectedPricingTier", JSON.stringify(payload));
      document.dispatchEvent(new CustomEvent("lgc:pricing-tier-change", { detail: payload }));
    });
  });
};

const normalizePathFromUrl = (url) => {
  if (!url) {
    return "";
  }

  try {
    return new URL(url, window.location.origin).pathname;
  } catch (_) {
    return "";
  }
};

const getGradingCartLockState = async () => {
  const cartDataUrl = `${window.routes?.cart_url || "/cart"}.js`;

  try {
    const response = await fetch(cartDataUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      return {
        hasExistingMain: false,
        allowedPaths: new Set(),
      };
    }

    const cart = await response.json();
    const items = Array.isArray(cart?.items) ? cart.items : [];

    const gradingMainItems = items.filter((item) => item?.properties?._lgc_item_type === "main");
    const allowedPaths = new Set();

    gradingMainItems.forEach((item) => {
      const itemPath = normalizePathFromUrl(item?.url || item?.product_url);
      if (itemPath) {
        allowedPaths.add(itemPath);
      }

      if (item?.handle) {
        allowedPaths.add(`/products/${item.handle}`);
      }
    });

    return {
      hasExistingMain: gradingMainItems.length > 0,
      allowedPaths,
    };
  } catch (_) {
    return {
      hasExistingMain: false,
      allowedPaths: new Set(),
    };
  }
};

const setPricingTierButtonsLocked = (lockState, scope = document) => {
  const links = scope.querySelectorAll("[data-lgc-tier-link]");
  const translatedLockLabel = scope.querySelector("[data-single-grading-lock-label]")?.dataset.singleGradingLockLabel;

  links.forEach((link) => {
    if (!link.dataset.lgcOriginalText) {
      link.dataset.lgcOriginalText = (link.textContent || "").trim();
    }

    const targetPath = normalizePathFromUrl(link.getAttribute("href") || "");
    const isAllowedMatch = targetPath && lockState.allowedPaths.has(targetPath);
    const shouldLock = lockState.hasExistingMain && !isAllowedMatch;

    if (shouldLock) {
      link.dataset.lgcLocked = "true";
      link.setAttribute("aria-disabled", "true");
      link.setAttribute("tabindex", "-1");
      link.classList.add("is-disabled");
      link.textContent = translatedLockLabel || "Only 1 grading at a time";
    } else {
      link.dataset.lgcLocked = "false";
      link.removeAttribute("aria-disabled");
      link.removeAttribute("tabindex");
      link.classList.remove("is-disabled");
      link.textContent = link.dataset.lgcOriginalText || "START HER";
    }
  });
};

const syncPricingTierButtonsLock = async (scope = document) => {
  const lockState = await getGradingCartLockState();
  setPricingTierButtonsLocked(lockState, scope);
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initEstimatedWorkdaysPopups(document);
    initPopupTextEditors();
    initPricingTierContext(document);
    syncPricingTierButtonsLock(document);
  });
} else {
  initEstimatedWorkdaysPopups(document);
  initPopupTextEditors();
  initPricingTierContext(document);
  syncPricingTierButtonsLock(document);
}

document.addEventListener("shopify:section:load", (event) => {
  initEstimatedWorkdaysPopups(event.target);
  initPopupTextEditors();
  initPricingTierContext(event.target);
  syncPricingTierButtonsLock(event.target);
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") {
    return;
  }

  document.querySelectorAll(".estimated-workdays__popup.is-open").forEach((popup) => {
    closePopup(popup);
  });
});
